import {
  createBalanceAdjustment,
  getOfficeBalanceAdjustments,
  getOfficeBillingHistory,
  type BalanceAdjustmentDoc,
  type BillingHistoryDoc,
  type NewBalanceAdjustmentPayload,
} from "../Services/Billing";
import { getOfficeById, updateOffice } from "../Services/Office";
import { isCancelledBillingDoc } from "./billingStatus";
import { isDirectPaymentDoc } from "./billingLedgerUtils";
import { DIRECT_PAYMENT_DESCRIPTION } from "./billingConstants";
import { officePartyKey } from "./accountingOfficeDetail";
import { parseCalendarDate } from "./dates";

export const BALANCE_ADJUSTMENT_KIND = {
  manualAddition: "manualAddition",
  manualDeduction: "manualDeduction",
  appliedToInvoice: "appliedToInvoice",
  appliedDebtToInvoice: "appliedDebtToInvoice",
  directPayment: "directPayment",
  invoiceReversal: "invoiceReversal",
  directPaymentReversal: "directPaymentReversal",
  invoiceEditAdjustment: "invoiceEditAdjustment",
  directPaymentEditAdjustment: "directPaymentEditAdjustment",
} as const;

export type BalanceAdjustmentKind =
  (typeof BALANCE_ADJUSTMENT_KIND)[keyof typeof BALANCE_ADJUSTMENT_KIND];

export const BALANCE_REASON_CODES = [
  { id: "manual_entry", name: "Manual Entry / Correction" },
  { id: "overpayment", name: "Overpayment" },
  { id: "refund", name: "Refund / Credit Issued" },
  { id: "write_off", name: "Write-off / Adjustment" },
  { id: "other", name: "Other" },
] as const;

export type BalanceReasonCode = (typeof BALANCE_REASON_CODES)[number]["id"];

export const BALANCE_LEDGER_TYPE_LABELS: Record<string, string> = {
  manualAddition: "Manual Addition",
  manualDeduction: "Manual Deduction",
  appliedToInvoice: "Applied to Invoice",
  appliedDebtToInvoice: "Applied to Invoice",
  appliedCredit: "Applied to Invoice",
  appliedDebt: "Applied to Invoice",
  directPayment: "Direct Payment",
  invoiceReversal: "Invoice Reversal",
  directPaymentReversal: "Direct Payment Reversal",
  invoiceEditAdjustment: "Invoice Adjustment",
  directPaymentEditAdjustment: "Direct Payment Adjustment",
};

export type BalanceLedgerEntryKind = string;

export type BalanceLedgerEntry = {
  id: string;
  sortAt: string;
  dateLabel: string;
  typeLabel: string;
  kind: BalanceLedgerEntryKind;
  user: string;
  reasonNotes: string;
  amount: number;
  source: "invoice" | "adjustment" | "payment";
  invoiceId?: string;
  paymentId?: string;
};

type PerformedByActor = {
  performedById: string;
  performedByName: string;
};

type AuthPersonLike = {
  honorary?: string;
  name?: string;
  middleInitial?: string;
  lastName?: string;
  suffix?: string;
  userId?: string;
  firmEmail?: string;
} | null | undefined;

type AuthTokenLike = {
  name?: string;
  oid?: string;
  emails?: string[];
} | null | undefined;

export function resolvePerformedBy(
  loggedPerson: AuthPersonLike,
  tokenDecoded: AuthTokenLike,
): PerformedByActor {
  const performedByName =
    (loggedPerson
      ? [
          loggedPerson.honorary,
          loggedPerson.name,
          loggedPerson.middleInitial,
          loggedPerson.lastName,
          loggedPerson.suffix,
        ]
          .filter((part) => part)
          .join(" ")
          .trim()
      : "") ||
    tokenDecoded?.name ||
    "Staff";

  const performedById =
    loggedPerson?.userId ?? tokenDecoded?.oid ?? "unknown";

  return { performedById, performedByName };
}

export function getAvailableCredit(balance: number | undefined | null): number {
  const value = Number(balance ?? 0);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, -value);
}

export function getMembershipBalance(balance: number | undefined | null): number {
  const value = Number(balance ?? 0);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, value);
}

export function officeBalanceIsDebt(balance: number | undefined | null): boolean {
  return Number(balance ?? 0) > 0;
}

export function formatBalanceReasonCode(code: string | undefined): string {
  const match = BALANCE_REASON_CODES.find((item) => item.id === code);
  return match?.name ?? code ?? "";
}

export function formatLedgerDateTime(value: string | undefined | null): string {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "—";
  }

  const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(raw);
  if (dateOnlyMatch) {
    const parsed = parseCalendarDate(raw);
    if (!parsed) {
      return raw;
    }
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function generateBalanceAdjustmentId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `balance-adj-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function resolveBillingDocLedgerTimestamp(
  doc: BillingHistoryDoc,
  calendarDate?: string,
): string {
  const cosmosTs = Number((doc as { _ts?: number })._ts ?? 0);
  if (cosmosTs > 0) {
    return new Date(cosmosTs * 1000).toISOString();
  }

  const createdDate = Number((doc as { createdDate?: number }).createdDate ?? 0);
  if (createdDate > 0) {
    return new Date(createdDate).toISOString();
  }

  const date = String(calendarDate ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  return new Date().toISOString();
}

export function resolveAppliedDebtFromInvoice(
  invoice: BillingHistoryDoc,
): number {
  const item = (invoice.items ?? []).find((i) => i.code === "previousBalance");
  return Number(item?.amount) || 0;
}

function isAnnualInvoice(invoice: BillingHistoryDoc): boolean {
  return invoice.context?.kind === "membershipDues";
}

function ledgerKindForAdjustment(adjustment: BalanceAdjustmentDoc): string {
  if (adjustment.kind === "appliedToInvoice") {
    return "appliedCredit";
  }
  if (adjustment.kind === "appliedDebtToInvoice") {
    return "appliedDebt";
  }
  return adjustment.kind;
}

function isCreditPositiveLedgerKind(kind: string): boolean {
  return (
    kind === BALANCE_ADJUSTMENT_KIND.manualAddition ||
    kind === BALANCE_ADJUSTMENT_KIND.directPayment ||
    kind === BALANCE_ADJUSTMENT_KIND.directPaymentReversal
  );
}

export function buildBalanceLedgerEntries(
  adjustments: BalanceAdjustmentDoc[],
): BalanceLedgerEntry[] {
  return adjustments.map((adjustment) => {
    const kind = ledgerKindForAdjustment(adjustment);
    const typeLabel =
      BALANCE_LEDGER_TYPE_LABELS[adjustment.kind] ??
      BALANCE_LEDGER_TYPE_LABELS[kind] ??
      adjustment.kind;
    const sourceLabel = String(adjustment.sourceLabel ?? "").trim();
    const reason = formatBalanceReasonCode(adjustment.reasonCode);
    const notes = String(adjustment.notes ?? "").trim();
    const reasonNotes =
      sourceLabel ||
      (notes ? (reason ? `${reason} — ${notes}` : notes) : reason) ||
      typeLabel;

    return {
      id: adjustment.id,
      sortAt: adjustment.performedAt,
      dateLabel: formatLedgerDateTime(adjustment.performedAt),
      typeLabel,
      kind,
      user: adjustment.performedByName || "Staff",
      reasonNotes,
      amount: Number(adjustment.amount) || 0,
      source: "adjustment",
      invoiceId: adjustment.invoiceId,
      paymentId: adjustment.paymentId,
    };
  });
}

export { isCreditPositiveLedgerKind };

function buildInvoiceBalanceLedgerEntries(
  invoices: BillingHistoryDoc[],
  coveredInvoiceIds: Set<string>,
): BalanceLedgerEntry[] {
  const entries: BalanceLedgerEntry[] = [];

  for (const invoice of invoices) {
    if (invoice.type !== "invoice" || isCancelledBillingDoc(invoice)) {
      continue;
    }
    if (!isAnnualInvoice(invoice)) {
      continue;
    }
    if (coveredInvoiceIds.has(invoice.id)) {
      continue;
    }

    const creditApplied = Number(invoice.creditApplied) || 0;
    const appliedDebt = resolveAppliedDebtFromInvoice(invoice);
    const invoiceRef = invoice.invoiceNumber?.trim() || invoice.id;
    const sortAt = resolveBillingDocLedgerTimestamp(invoice, invoice.issueDate);

    if (creditApplied > 0) {
      entries.push({
        id: `invoice-credit-${invoice.id}`,
        sortAt,
        dateLabel: formatLedgerDateTime(sortAt),
        typeLabel: BALANCE_LEDGER_TYPE_LABELS.appliedCredit,
        kind: "appliedCredit",
        user: "System",
        reasonNotes: `Applied to ${invoiceRef}`,
        amount: -creditApplied,
        source: "invoice",
        invoiceId: invoice.id,
      });
    }

    if (appliedDebt > 0) {
      entries.push({
        id: `invoice-debt-${invoice.id}`,
        sortAt,
        dateLabel: formatLedgerDateTime(sortAt),
        typeLabel: BALANCE_LEDGER_TYPE_LABELS.appliedDebt,
        kind: "appliedDebt",
        user: "System",
        reasonNotes: `Membership balance charged on ${invoiceRef}`,
        amount: appliedDebt,
        source: "invoice",
        invoiceId: invoice.id,
      });
    }
  }

  return entries;
}

function buildDirectPaymentBalanceLedgerEntries(
  payments: BillingHistoryDoc[],
  coveredPaymentIds: Set<string>,
): BalanceLedgerEntry[] {
  const entries: BalanceLedgerEntry[] = [];

  for (const payment of payments) {
    if (payment.type !== "payment") {
      continue;
    }
    if (!isDirectPaymentDoc(payment) || isCancelledBillingDoc(payment)) {
      continue;
    }
    if (coveredPaymentIds.has(payment.id)) {
      continue;
    }

    const amount = Number(payment.amount) || 0;
    if (amount <= 0) {
      continue;
    }

    const sortAt = resolveBillingDocLedgerTimestamp(
      payment,
      payment.paymentDate,
    );
    const description =
      payment.description?.trim() || DIRECT_PAYMENT_DESCRIPTION;
    const reference = payment.reference?.trim();
    const method = payment.method?.trim();
    const detailParts = [description];
    if (reference) {
      detailParts.push(reference);
    }
    if (method) {
      detailParts.push(method);
    }

    entries.push({
      id: `direct-payment-${payment.id}`,
      sortAt,
      dateLabel: formatLedgerDateTime(sortAt),
      typeLabel: BALANCE_LEDGER_TYPE_LABELS.directPayment,
      kind: "directPayment",
      user: "Staff",
      reasonNotes: detailParts.join(" · "),
      amount,
      source: "payment",
      paymentId: payment.id,
    });
  }

  return entries;
}

function collectCoveredSourceIds(adjustments: BalanceAdjustmentDoc[]): {
  invoiceIds: Set<string>;
  paymentIds: Set<string>;
} {
  const invoiceIds = new Set<string>();
  const paymentIds = new Set<string>();
  for (const adjustment of adjustments) {
    if (adjustment.invoiceId) {
      invoiceIds.add(adjustment.invoiceId);
    }
    if (adjustment.paymentId) {
      paymentIds.add(adjustment.paymentId);
    }
  }
  return { invoiceIds, paymentIds };
}

export function mergeBalanceLedgerEntries(
  ...entryGroups: BalanceLedgerEntry[][]
): BalanceLedgerEntry[] {
  return entryGroups.flat().sort((a, b) => {
    const aTime = new Date(a.sortAt).getTime();
    const bTime = new Date(b.sortAt).getTime();
    if (bTime !== aTime) {
      return bTime - aTime;
    }
    return a.id.localeCompare(b.id);
  });
}

export function matchesBalanceLedgerSearch(
  entry: BalanceLedgerEntry,
  query: string,
): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  const haystack = [
    entry.typeLabel,
    entry.user,
    entry.reasonNotes,
    entry.dateLabel,
    String(entry.amount),
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalized);
}

export type RecordOfficeBalanceMovementInput = {
  officeId: string;
  firmId?: string | null;
  currency?: string;
  balanceDelta: number;
  ledgerAmount: number;
  kind: BalanceAdjustmentKind | string;
  reasonCode?: string;
  notes?: string;
  sourceLabel?: string;
  invoiceId?: string;
  paymentId?: string;
  performedById: string;
  performedByName: string;
  performedAt?: string;
};

export async function recordOfficeBalanceMovement(
  input: RecordOfficeBalanceMovementInput,
): Promise<BalanceAdjustmentDoc> {
  const balanceDelta = Math.round(input.balanceDelta * 100) / 100;
  const ledgerAmount = Math.round(input.ledgerAmount * 100) / 100;
  if (balanceDelta === 0) {
    throw new Error("Balance adjustment must be non-zero.");
  }

  const rawOffice = await getOfficeById(input.officeId);
  if (!rawOffice) {
    throw new Error("Office not found.");
  }

  const previousBalance = Number(rawOffice.balance ?? 0);
  const newBalance = Math.round((previousBalance + balanceDelta) * 100) / 100;

  const payload: NewBalanceAdjustmentPayload = {
    id: generateBalanceAdjustmentId(),
    type: "balanceAdjustment",
    partyKey: officePartyKey(input.officeId),
    partyType: "office",
    partyId: input.officeId,
    officeId: input.officeId,
    firmId: input.firmId ?? rawOffice.firm ?? null,
    currency: input.currency ?? "USD",
    amount: ledgerAmount,
    balanceDelta,
    kind: input.kind,
    reasonCode: input.reasonCode,
    notes: input.notes?.trim() || undefined,
    sourceLabel: input.sourceLabel?.trim() || undefined,
    performedAt: input.performedAt ?? new Date().toISOString(),
    performedById: input.performedById,
    performedByName: input.performedByName,
    invoiceId: input.invoiceId,
    paymentId: input.paymentId,
  };

  await updateOffice({
    ...rawOffice,
    balance: newBalance,
  });

  try {
    await createBalanceAdjustment(payload);
  } catch (error) {
    await updateOffice({
      ...rawOffice,
      balance: previousBalance,
    });
    throw error;
  }

  return payload;
}

export type ManualBalanceAdjustmentInput = {
  officeId: string;
  firmId?: string | null;
  currency?: string;
  direction: "add" | "deduct";
  amount: number;
  reasonCode: string;
  notes?: string;
  performedById: string;
  performedByName: string;
};

export async function applyManualBalanceAdjustment(
  input: ManualBalanceAdjustmentInput,
): Promise<BalanceAdjustmentDoc> {
  const amount = Math.round(input.amount * 100) / 100;
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Amount must be greater than zero.");
  }

  const reasonCode = String(input.reasonCode ?? "").trim();
  if (!reasonCode) {
    throw new Error("Reason code is required.");
  }

  const balanceDelta = input.direction === "add" ? -amount : amount;
  const ledgerAmount = input.direction === "add" ? amount : -amount;
  const kind =
    input.direction === "add"
      ? BALANCE_ADJUSTMENT_KIND.manualAddition
      : BALANCE_ADJUSTMENT_KIND.manualDeduction;

  return recordOfficeBalanceMovement({
    officeId: input.officeId,
    firmId: input.firmId,
    currency: input.currency,
    balanceDelta,
    ledgerAmount,
    kind,
    reasonCode,
    notes: input.notes,
    performedById: input.performedById,
    performedByName: input.performedByName,
  });
}

export type InvoiceBalanceMovementInput = {
  officeId: string;
  firmId?: string | null;
  invoiceId: string;
  invoiceRef: string;
  appliedCredit: number;
  appliedDebt: number;
  performedById: string;
  performedByName: string;
};

export async function recordInvoiceBalanceMovements(
  input: InvoiceBalanceMovementInput,
): Promise<void> {
  const appliedCredit = Math.round(input.appliedCredit * 100) / 100;
  const appliedDebt = Math.round(input.appliedDebt * 100) / 100;
  const invoiceRef = input.invoiceRef.trim() || input.invoiceId;

  if (appliedCredit > 0) {
    await recordOfficeBalanceMovement({
      officeId: input.officeId,
      firmId: input.firmId,
      balanceDelta: appliedCredit,
      ledgerAmount: -appliedCredit,
      kind: BALANCE_ADJUSTMENT_KIND.appliedToInvoice,
      reasonCode: "system_invoice",
      sourceLabel: `Applied to ${invoiceRef}`,
      invoiceId: input.invoiceId,
      performedById: input.performedById,
      performedByName: input.performedByName,
    });
  }

  if (appliedDebt > 0) {
    await recordOfficeBalanceMovement({
      officeId: input.officeId,
      firmId: input.firmId,
      balanceDelta: -appliedDebt,
      ledgerAmount: appliedDebt,
      kind: BALANCE_ADJUSTMENT_KIND.appliedDebtToInvoice,
      reasonCode: "system_invoice",
      sourceLabel: `Membership balance charged on ${invoiceRef}`,
      invoiceId: input.invoiceId,
      performedById: input.performedById,
      performedByName: input.performedByName,
    });
  }
}

export async function recordInvoiceBalanceDeltaMovement(
  input: InvoiceBalanceMovementInput & { balanceDelta: number },
): Promise<void> {
  const balanceDelta = Math.round(input.balanceDelta * 100) / 100;
  if (balanceDelta === 0) {
    return;
  }

  const invoiceRef = input.invoiceRef.trim() || input.invoiceId;
  const ledgerAmount = -balanceDelta;

  await recordOfficeBalanceMovement({
    officeId: input.officeId,
    firmId: input.firmId,
    balanceDelta,
    ledgerAmount,
    kind: BALANCE_ADJUSTMENT_KIND.invoiceEditAdjustment,
    reasonCode: "system_invoice",
    sourceLabel: `Invoice adjustment on ${invoiceRef}`,
    invoiceId: input.invoiceId,
    performedById: input.performedById,
    performedByName: input.performedByName,
  });
}

export async function recordInvoiceBalanceReversal(
  input: InvoiceBalanceMovementInput,
): Promise<void> {
  const appliedCredit = Number(input.appliedCredit) || 0;
  const appliedDebt = Number(input.appliedDebt) || 0;
  const reversal = -(appliedCredit - appliedDebt);
  if (reversal === 0) {
    return;
  }

  const invoiceRef = input.invoiceRef.trim() || input.invoiceId;

  await recordOfficeBalanceMovement({
    officeId: input.officeId,
    firmId: input.firmId,
    balanceDelta: reversal,
    ledgerAmount: -reversal,
    kind: BALANCE_ADJUSTMENT_KIND.invoiceReversal,
    reasonCode: "system_invoice",
    sourceLabel: `Reversed balance applied on ${invoiceRef}`,
    invoiceId: input.invoiceId,
    performedById: input.performedById,
    performedByName: input.performedByName,
  });
}

export async function recordDirectPaymentBalanceMovement(input: {
  officeId: string;
  firmId?: string | null;
  paymentId: string;
  amount: number;
  sourceLabel?: string;
  kind?: BalanceAdjustmentKind | string;
  performedById: string;
  performedByName: string;
}): Promise<void> {
  const amount = Math.round(input.amount * 100) / 100;
  if (amount === 0) {
    return;
  }

  await recordOfficeBalanceMovement({
    officeId: input.officeId,
    firmId: input.firmId,
    balanceDelta: -amount,
    ledgerAmount: amount,
    kind: input.kind ?? BALANCE_ADJUSTMENT_KIND.directPayment,
    reasonCode: "system_payment",
    sourceLabel: input.sourceLabel,
    paymentId: input.paymentId,
    performedById: input.performedById,
    performedByName: input.performedByName,
  });
}

export async function loadOfficeBalanceLedger(
  officeId: string,
): Promise<BalanceLedgerEntry[]> {
  const adjustments = await getOfficeBalanceAdjustments(officeId);
  const persisted = buildBalanceLedgerEntries(adjustments);
  const { invoiceIds, paymentIds } = collectCoveredSourceIds(adjustments);

  const billingHistory = await getOfficeBillingHistory(officeId);
  const invoices = billingHistory.filter((doc) => doc.type === "invoice");
  const payments = billingHistory.filter((doc) => doc.type === "payment");

  return mergeBalanceLedgerEntries(
    persisted,
    buildInvoiceBalanceLedgerEntries(invoices, invoiceIds),
    buildDirectPaymentBalanceLedgerEntries(payments, paymentIds),
  );
}
